import { ApolloLink, Observable } from '@apollo/client';
import type { Operation, FetchResult } from '@apollo/client';
import { print } from 'graphql';

export class NativeWebSocketLink extends ApolloLink {
  private wsUrl: string;
  private httpUrl: string;

  constructor(wsUrl: string, httpUrl: string) {
    super();
    this.wsUrl = wsUrl;
    this.httpUrl = httpUrl;
  }

  public override request(operation: Operation): Observable<FetchResult> {
    const { definitions } = operation.query;
    const isSubscription = definitions.some(
      (def) => def.kind === 'OperationDefinition' && def.operation === 'subscription'
    );

    if (!isSubscription) {
      return new Observable<FetchResult>((observer) => {
        observer.error(new Error('NativeWebSocketLink only supports subscription operations'));
      });
    }

    return new Observable<FetchResult>((observer) => {
      let ws: WebSocket | null = null;
      let isClosed = false;

      const contextHeaders = operation.getContext?.()?.headers || {};
      const token = localStorage.getItem('auth_token');
      const headers: Record<string, string> = {
        'content-type': 'application/json',
        'accept': 'application/json',
        ...contextHeaders,
      };
      if (token && !headers['authorization'] && !headers['Authorization']) {
        headers['authorization'] = `Bearer ${token}`;
      }

      // 1. Initialize subscription channel name via HTTP POST request
      fetch(this.httpUrl, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          query: print(operation.query),
          variables: operation.variables,
        }),
      })
        .then((res) => {
          if (!res.ok) {
            throw new Error(`HTTP error ${res.status}`);
          }
          return res.json();
        })
        .then((response) => {
          if (isClosed) return;

          const channels = response.extensions?.lighthouse_subscriptions?.channels;
          if (!channels) {
            let errMsg = 'No subscription channels returned in extensions';
            if (response.error?.message) {
              errMsg = response.error.message;
            } else if (typeof response.error === 'string') {
              errMsg = response.error;
            } else if (response.errors?.[0]?.message) {
              errMsg = response.errors[0].message;
            } else if (response.message) {
              errMsg = response.message;
            }
            observer.error(new Error(errMsg));
            return;
          }

          // Get the first subscription channel name (e.g. notificationAdded)
          const channelName = Object.values(channels)[0] as string;
          if (!channelName) {
            observer.error(new Error('Subscription channel name is empty'));
            return;
          }

          // 2. Establish connection to Laravel Reverb / WebSocket
          ws = new WebSocket(this.wsUrl);

          ws.onopen = () => {
            if (isClosed) {
              ws?.close();
              return;
            }
            // Send Pusher-protocol subscribe event
            ws?.send(
              JSON.stringify({
                event: 'pusher:subscribe',
                data: {
                  channel: channelName,
                },
              })
            );
          };

          ws.onmessage = (event) => {
            if (isClosed) return;
            try {
              const payload = JSON.parse(event.data);
              // Handle subscription messages matching pusher event structure
              if (payload.event === 'lighthouse-subscription') {
                const innerData = JSON.parse(payload.data);
                if (innerData.result) {
                  observer.next(innerData.result);
                }
              }
            } catch (err) {
              console.error('Error parsing WebSocket frame', err);
            }
          };

          ws.onerror = (err) => {
            if (isClosed) return;
            observer.error(err);
          };

          ws.onclose = () => {
            if (isClosed) return;
            observer.complete();
          };
        })
        .catch((err) => {
          if (isClosed) return;
          const errMsg = err instanceof Error ? err.message : String(err);
          observer.error(new Error(errMsg));
        });

      // Cleanup function
      return () => {
        isClosed = true;
        if (ws) {
          ws.close();
        }
      };
    });
  }
}
